import { Paper } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { Fragment } from "react";

import { formatItem, formatPenalty } from "@framework/exchange";
import { StakingDepositStatus, IStakingDeposit, IStakingRule } from "@framework/types";
import { RichTextDisplay } from "@dzambalaorg/mui-rte";
import { AddressLink } from "@dzambalaorg/mui-scanner";
import { Table } from "@dzambalaorg/ui-kit";

import { normalizeDuration } from "../../../../../../utils/time";

export interface IStakingRuleExt extends IStakingRule {
  stakes: Array<IStakingDeposit>;
}

export interface IStakingDialogViewProps {
  viewItem: IStakingRuleExt;
}

export const StakingDialogView = (props: IStakingDialogViewProps) => {
  const { viewItem } = props;

  const {
    title,
    description,
    deposit,
    reward,
    durationAmount,
    durationUnit,
    penalty,
    recurrent,
    contract,
    stakes = [],
  } = viewItem;

  // STATS
  const totalStakesCount = stakes.length;
  const activeStakesCount = stakes.filter(stake => stake.stakingDepositStatus === StakingDepositStatus.ACTIVE).length;
  let totalComp = deposit ? deposit.components : [];
  if (deposit) {
    totalComp = deposit.components.map(item => {
      return { ...item, amount: (BigInt(item.amount) * BigInt(activeStakesCount)).toString() };
    });
  }

  const { formatMessage } = useIntl();

  return (
    <Fragment>
      <Table.Container component={Paper}>
        <Table aria-label="staking rules table">
          <Table.Body>
            <Table.Row>
              <Table.Cell component="th" scope="row">
                <FormattedMessage id="form.labels.title" />
              </Table.Cell>
              <Table.Cell align="right">{title}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell component="th" scope="row">
                <FormattedMessage id="form.labels.description" />
              </Table.Cell>
              <Table.Cell align="right">
                <RichTextDisplay data={description} />
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell component="th" scope="row">
                <FormattedMessage id="form.labels.contract" />
              </Table.Cell>
              <Table.Cell align="right">{contract!.title}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell component="th" scope="row">
                <FormattedMessage id="form.labels.address" />
              </Table.Cell>
              <Table.Cell align="right">
                <AddressLink address={contract?.address} />
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell component="th" scope="row">
                <FormattedMessage id="form.labels.deposit" />
              </Table.Cell>
              <Table.Cell align="right">{formatItem(deposit)}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell component="th" scope="row">
                <FormattedMessage id="form.labels.reward" />
              </Table.Cell>
              <Table.Cell align="right">{formatItem(reward)}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell component="th" scope="row">
                <FormattedMessage id="form.labels.durationAmount" />
              </Table.Cell>
              <Table.Cell align="right">
                {formatMessage(
                  { id: `enums.durationUnit.${durationUnit}` },
                  { count: normalizeDuration({ durationAmount, durationUnit }) },
                )}
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell component="th" scope="row">
                <FormattedMessage id="form.labels.penalty" />
              </Table.Cell>
              <Table.Cell align="right">{formatPenalty(penalty)}%</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell component="th" scope="row">
                <FormattedMessage id="form.labels.recurrent" />
              </Table.Cell>
              <Table.Cell align="right">{recurrent ? "yes" : "no"}</Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </Table.Container>
      {totalStakesCount > 0 ? (
        <Table.Container component={Paper}>
          <Table aria-label="staking rule history">
            <Table.Body>
              <Table.Row>
                <Table.Cell align="center" scope="row">
                  <FormattedMessage id="form.labels.stakeStat" />
                </Table.Cell>
              </Table.Row>
              {totalStakesCount > 0 ? (
                <Table.Row>
                  <Table.Cell component="th" scope="row">
                    <FormattedMessage id="form.labels.stakesTotal" />
                  </Table.Cell>
                  <Table.Cell align="right">{totalStakesCount}</Table.Cell>
                </Table.Row>
              ) : null}
              {activeStakesCount > 0 ? (
                <Table.Row>
                  <Table.Cell component="th" scope="row">
                    <FormattedMessage id="form.labels.stakesCount" />
                  </Table.Cell>
                  <Table.Cell align="right">{activeStakesCount}</Table.Cell>
                </Table.Row>
              ) : null}
              {activeStakesCount > 0 ? (
                <Table.Row>
                  <Table.Cell component="th" scope="row">
                    <FormattedMessage id="form.labels.tvl" />
                  </Table.Cell>
                  <Table.Cell align="right">{formatItem({ components: totalComp })}</Table.Cell>
                </Table.Row>
              ) : null}
            </Table.Body>
          </Table>
        </Table.Container>
      ) : null}
    </Fragment>
  );
};
